/**
 * Sentry initialization for the whole product.
 *
 * Imported as the very first line of `main.tsx` — ahead of every other import —
 * because ES module side effects run before the importing module's first
 * statement. `App.tsx` builds the Auth0 client at module scope, and a throw
 * there is exactly the white-screen-on-boot failure we most want reported.
 *
 * The shell is the only app we deploy: it owns the router and lazily mounts
 * studio5 or studio-classic per route, so this is the one `init` that runs in
 * production and it covers every route the shell renders, including the
 * in-document standalone builder. The builders at `MOBILE_BUILDER_URL` are
 * separate top-level documents and are instrumented in the builder-ui repo.
 */
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import {
  init,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
} from '@sentry/react';

// Two projects in two orgs, because Sentry implements EU data residency as a
// wholly separate org on `ingest.de.sentry.io` — an EU project cannot live in
// the US org. Picking by region keeps EU users' errors and recordings inside
// the EU, which is what the old code failed to do: it sent EU errors to the US
// project and merely disabled replay there.
//
// The US project is still named "studio5" in Sentry but is the product-wide
// one: the shell is the only app we deploy. It had been dark because the only
// code referencing it was studio5's `initializeErrorReporting`, called from a
// `main.tsx` that never runs in production. It replaces studio-classic's legacy
// DSN (project 1250866), which no longer resolves to a project in the org —
// which is why prod reported nothing despite classic's init running.
const SENTRY_DSN_US =
  'https://1c98d40640804fd2b55f99dd9d1a4e37@o10683.ingest.us.sentry.io/4503921308270592';
const SENTRY_DSN_EU =
  'https://00bdf3b85d8fee9225fb4c0f4291ebd5@o4510267316502529.ingest.de.sentry.io/4512024094900304';

// Preview links are prod builds served from a commit-SHA path, and the preprod
// facet also compiles with APPCUES_ENV=prod. Both would otherwise file their
// errors and replays against prod, polluting its alerting and quota.
const PREVIEW_PATTERN = /^\/index\.[\da-f]{40}\.html/;

const environment =
  PREVIEW_PATTERN.test(window.location.pathname) ||
  import.meta.env.APPCUES_FACET === 'preprod'
    ? 'preprod'
    : import.meta.env.APPCUES_ENV || 'staging';

// `AWS_REGION` is injected by vite.config.ts (see the opscues plugin), not
// resolved from SSM; CI sets `eu-central-1` on the EU jobs and everything else
// takes the `us-west-2` default.
//
// One map drives both the DSN and the replay gate, so the two cannot disagree
// about a region. A region we have no project for is not a hypothetical we can
// resolve at runtime, so it falls back to the US project for errors and is
// denied replay below.
const SENTRY_DSN_BY_REGION: Partial<Record<string, string>> = {
  'us-west-2': SENTRY_DSN_US,
  'eu-central-1': SENTRY_DSN_EU,
};

const region = import.meta.env.AWS_REGION;
const regionDsn = region ? SENTRY_DSN_BY_REGION[region] : undefined;
const dsn = regionDsn ?? SENTRY_DSN_US;

// Session replay records the DOM, so it records prod only — staging and preprod
// get errors and tracing but no replay, matching the LogRocket `isProd` gate
// this replaces.
//
// The region half is not about which regions may record — every region with a
// project of its own may, EU included. It requires that the DSN was chosen *for*
// this region rather than fallen back to: an unset or unrecognized `AWS_REGION`
// resolves to the US project, and recording an EU build into it would move EU
// DOM data to the US. An error is worth that risk, a full DOM recording is not,
// so an unknown region still reports but never records.
const replayAllowed = regionDsn !== undefined && environment === 'prod';

init({
  dsn,
  release: import.meta.env.COMMIT_HASH,
  environment,
  enabled: !import.meta.env.DEV,
  integrations: [
    reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      // `maskAttributes` REPLACES the SDK's defaults rather than extending them,
      // so `title` and `placeholder` must be re-listed. `auth-token` carries a
      // live Auth0 bearer token on <appcues-builder-standalone>; masking text and
      // inputs does not cover attributes. Note this cannot protect `src`/`href` —
      // rrweb resolves those before masking runs, so URL-borne secrets need
      // `block`, not this list.
      maskAttributes: ['title', 'placeholder', 'auth-token'],
      // The un-mask escape hatch has no SDK defaults; without declaring it here,
      // `data-sentry-unmask` silently does nothing. Beware it is broad: a matched
      // element has all of its attributes unmasked too, not just its text.
      unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
    }),
  ],
  tracesSampleRate: 0.25,
  // Record every prod session, in both regions. Deliberate — revisit once we
  // have seen real replay quota consumption.
  replaysSessionSampleRate: replayAllowed ? 1 : 0,
  // Inert while the session rate is 1 (session sampling is evaluated first and
  // never falls through to buffer mode). Kept so lowering the rate above is a
  // one-line change that keeps error sessions at full capture.
  replaysOnErrorSampleRate: replayAllowed ? 1 : 0,
});
